import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Award, CheckCircle, Calendar, BookOpen } from 'lucide-react';

const PageHero = ({ title, breadcrumbs }) => (
  <div className="page-hero pt-[70px]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
            <Home size={14} /> Beranda
          </Link>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={14} />
              <span className={i === breadcrumbs.length - 1 ? 'text-white' : ''}>{bc}</span>
            </React.Fragment>
          ))}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white font-heading">{title}</h1>
      </motion.div>
    </div>
  </div>
);

const prodiList = [
  {
    nama: 'Prodi Kesehatan Masyarakat',
    sk: 'SK Dikti No. 93/D/O/2000',
    tanggal: '09 Juni 2001',
    akreditasi: 'Baik Sekali',
    noAkreditasi: '0935/LAM-PTKes/Akr/Sar/XI/2024',
    color: 'from-emerald-500 to-teal-600',
    icon: '🏥',
  },
  {
    nama: 'Prodi Ilmu Keperawatan',
    sk: 'SK Dikti No. 2807/DT/2001',
    tanggal: '30 Agustus 2001',
    akreditasi: 'Baik Sekali',
    noAkreditasi: '0887/LAM-PTKes/Akr/Sar/XII/2023',
    color: 'from-blue-500 to-blue-700',
    icon: '🩺',
  },
  {
    nama: 'Prodi Farmasi',
    sk: 'SK Dikti No. 1596/D/T/2007',
    tanggal: '3 Juli 2007',
    akreditasi: 'Baik Sekali',
    noAkreditasi: '0670/LAM-PTKes/Akr/Sar/VIII/2022',
    color: 'from-orange-500 to-amber-600',
    icon: '💊',
  },
  {
    nama: 'Prodi Profesi Ners',
    sk: 'SK Dikti No. 267/D/O/2008',
    tanggal: '31 Desember 2008',
    akreditasi: 'Baik Sekali',
    noAkreditasi: '0888/LAM-PTKes/Akr/Pro/XII/2023',
    color: 'from-[#7B2FBE] to-[#9333EA]',
    icon: '👩‍⚕️',
  },
];

const milestones = [
  { year: '2000', title: 'Pendirian Yayasan', desc: 'Yayasan Harapan Ibu Jambi resmi berdiri pada tanggal 9 Juni 2000, menjadi fondasi bagi penyelenggaraan pendidikan tinggi kesehatan di Provinsi Jambi.' },
  { year: '2001', title: 'Pembukaan Prodi Pertama', desc: 'STIKES Harapan Ibu Jambi resmi beroperasi dengan membuka Prodi Kesehatan Masyarakat dan Prodi Ilmu Keperawatan, menjadi institusi pendidikan tinggi kesehatan pertama di Jambi.' },
  { year: '2007', title: 'Prodi Farmasi Dibuka', desc: 'Seiring meningkatnya kebutuhan tenaga farmasi, STIKES HI membuka Program Studi Farmasi berdasarkan SK Dikti No. 1596/D/T/2007.' },
  { year: '2008', title: 'Prodi Profesi Ners', desc: 'STIKES HI melengkapi layanan pendidikannya dengan membuka Program Profesi Ners, menjadikan institusi ini sebagai pusat pendidikan keperawatan yang komprehensif.' },
  { year: '2022-2024', title: 'Akreditasi Baik Sekali', desc: 'Seluruh 4 program studi berhasil meraih akreditasi "Baik Sekali" dari LAM-PTKes, pengakuan tertinggi atas kualitas pendidikan yang diselenggarakan STIKES HI.' },
];

const Sejarah = () => (
  <div data-testid="sejarah-page">
    <PageHero title="Sejarah Kampus" breadcrumbs={['Tentang Kami', 'Sejarah Kampus']} />

    <div className="bg-[#F8F7FF] dark:bg-[#121020]">

      {/* Narasi Pembuka */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white dark:bg-[#1A182E] rounded-3xl p-8 md:p-12 border border-purple-100 dark:border-purple-900/20 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <BookOpen size={20} className="text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-[#1A1A2E] dark:text-white font-heading">Sejarah Pendirian</h2>
              </div>
              <div className="space-y-4 text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                <p>
                  Sekolah Tinggi Ilmu Kesehatan (STIKES) Harapan Ibu Jambi sebagai salah satu PTS di Provinsi Jambi selalu berupaya memberikan pelayanan terbaik bagi mahasiswanya maupun seluruh stakeholder.
                </p>
                <p>
                  STIKES Harapan Ibu Jambi adalah institusi pendidikan tinggi di bidang kesehatan yang menghasilkan sarjana kesehatan yang profesional. STIKES Harapan Ibu Jambi berada di bawah payung <span className="font-semibold text-purple-600 dark:text-purple-400">Yayasan Harapan Ibu Jambi</span>, berdiri sejak tanggal <span className="font-semibold text-purple-600 dark:text-purple-400">9 Juni 2000</span>.
                </p>
                <p>
                  Saat ini STIKES Harapan Ibu Jambi telah mempunyai <span className="font-semibold text-purple-600 dark:text-purple-400">4 (empat) Program Studi</span> yang seluruhnya telah terakreditasi "Baik Sekali" oleh LAM-PTKes.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Studi */}
      <section className="py-4 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">Program Studi</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">Seluruh program studi terakreditasi "Baik Sekali" oleh LAM-PTKes</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {prodiList.map((prodi, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white dark:bg-[#1A182E] rounded-2xl overflow-hidden border border-purple-100 dark:border-purple-900/20 shadow-sm hover:shadow-md transition-all"
              >
                {/* Header */}
                <div className={`bg-gradient-to-r ${prodi.color} p-4 flex items-center gap-3`}>
                  <span className="text-2xl">{prodi.icon}</span>
                  <h3 className="text-white font-bold text-sm leading-tight">{prodi.nama}</h3>
                </div>

                {/* Body */}
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <Calendar size={14} className="text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Dasar Pendirian</p>
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{prodi.sk}</p>
                      <p className="text-xs text-gray-500">{prodi.tanggal}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Award size={14} className="text-purple-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Akreditasi LAM-PTKes</p>
                      <span className="inline-block text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 mt-0.5">
                        ✓ {prodi.akreditasi}
                      </span>
                      <p className="text-xs text-gray-400 mt-1 break-all">No. {prodi.noAkreditasi}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 bg-white dark:bg-[#100E1C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white font-heading">Perjalanan Institusi</h2>
          </motion.div>

          <div className="relative">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#7B2FBE] to-[#9333EA] transform md:-translate-x-px" />

            {milestones.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className={`relative md:flex mb-10 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                <div className={`md:w-5/12 ml-14 md:ml-0 ${i % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                  <div className="bg-[#F8F7FF] dark:bg-[#1A182E] rounded-2xl p-5 border border-purple-100 dark:border-purple-900/20 hover:shadow-md transition-shadow">
                    <h3 className="font-bold text-[#1A1A2E] dark:text-white mb-2 font-heading">{m.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{m.desc}</p>
                  </div>
                </div>

                {/* Year badge */}
                <div className="hidden md:flex w-2/12 items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center z-10 shadow-lg shadow-purple-500/30">
                    <span className="text-white font-bold text-xs text-center leading-tight px-1">{m.year}</span>
                  </div>
                </div>

                {/* Mobile badge */}
                <div className="absolute left-0 top-4 md:hidden">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-[10px] text-center leading-tight px-1">{m.year}</span>
                  </div>
                </div>

                <div className="md:w-5/12" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '2000', label: 'Tahun Berdiri', icon: <Calendar size={20} /> },
              { value: '4', label: 'Program Studi', icon: <BookOpen size={20} /> },
              { value: '4×', label: 'Akreditasi Baik Sekali', icon: <Award size={20} /> },
              { value: '24+', label: 'Tahun Pengalaman', icon: <CheckCircle size={20} /> },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card p-5 rounded-2xl text-center"
              >
                <div className="flex justify-center mb-2 text-purple-500">{s.icon}</div>
                <div className="text-2xl font-bold text-[#7B2FBE] dark:text-purple-400 font-heading">{s.value}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  </div>
);

export default Sejarah;